import { useState } from "react";
import ReviewListItem from "./review-list-item";
import type { Review, ReviewContentType } from "../types";
import { DeleteConfirmationModal } from "./delete-confirmation-modal";
import { getMuxThumbnailUrl } from "../utils/mux-urls";

const ReviewSkeletonLoading = () => {
	return (
		<div className="divide-y divide-gray-200">
			<div className="animate-pulse space-y-4">
				{[1, 2, 3].map((i) => (
					<div key={i} className="py-6 space-y-4">
						<div className="flex items-start justify-between">
							<div className="flex-1 space-y-3">
								<div className="flex items-center gap-3">
									<div className="h-5 bg-gray-200 rounded w-32" />
									<div className="h-4 bg-gray-200 rounded w-24" />
								</div>
								<div className="h-4 bg-gray-200 rounded w-48" />
								<div className="flex items-center gap-2">
									<div className="h-4 bg-gray-200 rounded w-20" />
								</div>
							</div>
						</div>
						<div className="space-y-2">
							<div className="h-4 bg-gray-200 rounded w-3/4" />
							<div className="h-4 bg-gray-200 rounded w-full" />
							<div className="h-4 bg-gray-200 rounded w-5/6" />
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export interface ReviewListProps {
	reviews: Review[];
	onDelete?: (id: string) => void;
	onEdit?: (id: string) => void;
	onApprove?: (id: string) => void;
	onUnapprove?: (id: string) => void;
	onClick?: (id: string) => void;
	className?: string;
	showDeleteButton?: boolean;
	showEditButton?: boolean;
	showApprovalButtons?: boolean;
	isLoading?: boolean;
	statusChangingReviewId?: string | null;
	isDemoMode?: boolean;
}

export default function ReviewList({
	reviews,
	onDelete,
	onEdit,
	onApprove,
	onUnapprove,
	onClick,
	className = "",
	showDeleteButton = true,
	showEditButton = true,
	showApprovalButtons = true,
	isLoading = false,
	statusChangingReviewId,
	isDemoMode = false,
}: ReviewListProps) {
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [selectedReview, setSelectedReview] = useState<Review | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const handleDeleteClick = (review: Review) => {
		setSelectedReview(review);
		setDeleteModalOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (!selectedReview || !onDelete) return;

		setIsDeleting(true);
		try {
			await onDelete(selectedReview.id);
			// Only close modal on successful deletion
			setDeleteModalOpen(false);
			setSelectedReview(null);
		} catch (error) {
			console.error("Failed to delete review:", error);
			alert("Failed to delete review. Please try again.");
		} finally {
			setIsDeleting(false);
		}
	};

	// Helper to get video info from review
	const getVideoInfo = (review: Review) => {
		// If video is still processing, return processing state
		if (review.video_status === "processing") {
			return { playbackId: null, thumbnailUrl: null, isProcessing: true };
		}

		// Get playback ID and generate thumbnail URL
		const playbackId = review.mux_playback_id || null;
		const thumbnailUrl = review.video_thumbnail_url || (playbackId ? getMuxThumbnailUrl(playbackId) : null);

		return { playbackId, thumbnailUrl, isProcessing: false };
	};

	if (isLoading) {
		return <ReviewSkeletonLoading />;
	}

	return (
		<>
			<div className={`space-y-2 ${className}`}>
				{reviews.map((review) => {
					const { playbackId, thumbnailUrl, isProcessing } = getVideoInfo(review);
					return (
						<ReviewListItem
							key={review.id}
							rating={review.rating}
							type={review.type}
							testimonial={review.content_text}
							date={formatDate(review.date)}
							source={review.source}
							tags={review.tags}
							status={review.status as "draft" | "pending" | "approved" | "unapproved"}
							contentType={review.content_type as ReviewContentType}
							audioUrl={review.audio_url}
							audioDuration={review.audio_duration}
							muxPlaybackId={playbackId}
							videoThumbnailUrl={thumbnailUrl}
							videoDuration={review.video_duration}
							videoStatus={review.video_status}
							isVideoProcessing={isProcessing}
							customerName={review.customer_name}
							customerHeadline={review.customer_headline}
							customerJobTitle={review.customer_job_title}
							customerAvatarUrl={review.customer_avatar_url}
							companyName={review.customer_company_name}
							companyLogoUrl={review.customer_company_logo_url}
							onDelete={!isDemoMode && onDelete ? () => handleDeleteClick(review) : undefined}
							showDeleteButton={!isDemoMode && showDeleteButton}
							onEdit={!isDemoMode && onEdit ? () => onEdit(review.id) : undefined}
							showEditButton={!isDemoMode && showEditButton}
							onApprove={onApprove ? () => onApprove(review.id) : undefined}
							onUnapprove={!isDemoMode && onUnapprove ? () => onUnapprove(review.id) : undefined}
							showApprovalButtons={showApprovalButtons}
							onClick={!isDemoMode && onClick ? () => onClick(review.id) : undefined}
							isDemoMode={isDemoMode}
							isStatusChanging={statusChangingReviewId === review.id}
						/>
					);
				})}
			</div>

			<DeleteConfirmationModal
				open={deleteModalOpen}
				title="Delete Review"
				description="Are you sure you want to delete this review? This action cannot be undone."
				isLoading={isDeleting}
				onOpenChange={setDeleteModalOpen}
				onConfirm={handleDeleteConfirm}
			/>
		</>
	);
}
